<script setup lang="ts">
/*
 * Global failed-backend-response dialog. Mounted ONCE at the app root (see
 * App.vue) — never per-route — so it floats above whatever view the user is
 * on. It renders the shared Modal primitive whenever the error-dialog store
 * holds an error.
 *
 * Explicit-close only: the Modal primitive already swallows overlay-click +
 * Esc unless `dismissible` is set, and we leave `dismissible` at its default
 * (false). Both the × and the footer "Close" button call `store.dismiss()`,
 * which ONLY clears the store. It never navigates, reloads, or unmounts the
 * current view — so half-filled forms and the current route are preserved.
 *
 * `message` is the prominent friendly explanation; `detail` (the raw backend
 * message / code) sits below it as quieter secondary text and is shown only
 * when present.
 */
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { Modal, Button } from '@/ui';
import { useErrorDialogStore } from '@/stores/errorDialog';

const { t } = useI18n();
const store = useErrorDialogStore();
const { current, isOpen } = storeToRefs(store);
</script>

<template>
  <Modal
    :open="isOpen"
    :title="current?.header"
    size="sm"
    data-testid="global-error-dialog"
    @close="store.dismiss()"
  >
    <div class="flex flex-col gap-3">
      <p class="text-md text-app-fg" data-testid="global-error-message">
        {{ current?.message }}
      </p>
      <p
        v-if="current?.detail"
        class="rounded-lg bg-slate-50 px-3 py-2 text-sm break-words text-app-muted"
        data-testid="global-error-detail"
      >
        {{ current.detail }}
      </p>
    </div>

    <template #footer>
      <Button variant="primary" data-testid="global-error-close" @click="store.dismiss()">
        {{ t('v2.errors.dialog.close') }}
      </Button>
    </template>
  </Modal>
</template>
